import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, X, Loader2,
  Zap, Shield, BarChart3, Store, Ticket, Banknote,
  Upload, Settings2, Users, Play, Globe, Building2,
  Landmark, Heart, Clock,
} from 'lucide-react';

function FadeIn({ children, delay = 0, className = '' }) {
  return (
    <div className={`animate-in fade-in slide-in-from-bottom-4 ${className}`} style={{ animationDelay: `${delay}s`, animationFillMode: 'both', animationDuration: '0.6s' }}>
      {children}
    </div>
  );
}

export default function EmployersPage() {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company_name: '', phone: '', company_size: '', role: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const openDemo = () => { setFormError(''); setShowDemo(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name || !formData.email || !formData.company_name || !formData.phone || !formData.company_size || !formData.role) {
      setFormError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'employers_page' }),
      });
    } catch (_) {}
    setSubmitting(false);
    setShowDemo(false);
    const name = formData.name;
    const email = formData.email;
    setFormData({ name: '', email: '', company_name: '', phone: '', company_size: '', role: '' });
    const params = new URLSearchParams({ name, email });
    window.open(`https://calendly.com/hello-thcohqq?${params.toString()}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="employers-page">

      {/* DEMO MODAL */}
      {showDemo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="demo-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDemo(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-7 animate-in fade-in zoom-in-95">
            <button onClick={() => setShowDemo(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#f9fafb] hover:bg-[#f0f0f0] flex items-center justify-center transition-colors" data-testid="demo-modal-close">
              <X className="w-4 h-4 text-[#6b7280]" />
            </button>
            <div className="mb-6">
              <img src="/pebbles-logo.svg" alt="Pebbles" className="h-6 mb-4" />
              <h3 className="text-xl font-extrabold text-[#1a1d1f]" style={{ fontFamily: 'Manrope' }}>Request a demo</h3>
              <p className="text-sm text-[#6b7280] mt-1">We'll model your specific salary bands and walk you through how it works.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Full Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Funke Adeyemi"
                  className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-name" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Work Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="funke@company.ng"
                  className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Company Name</label>
                  <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Acme Corp"
                    className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-company" />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Phone Number</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+234 801 234 5678"
                    className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Company Size</label>
                  <select value={formData.company_size} onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] appearance-none" data-testid="demo-size">
                    <option value="" disabled>Select</option>
                    <option value="1-10">1 - 10</option>
                    <option value="11-50">11 - 50</option>
                    <option value="51-200">51 - 200</option>
                    <option value="201-500">201 - 500</option>
                    <option value="500+">500+</option>
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Your Role</label>
                  <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] appearance-none" data-testid="demo-role">
                    <option value="" disabled>Select</option>
                    <option value="founder_ceo">Founder / CEO</option>
                    <option value="hr_lead">HR Lead / People Ops</option>
                    <option value="cfo_finance">CFO / Finance</option>
                    <option value="operations">Operations</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              {formError && <p className="text-xs text-red-500 font-medium" data-testid="demo-form-error">{formError}</p>}
              <Button type="submit" disabled={submitting} className="w-full bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-11 text-sm font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.25)] mt-2" data-testid="demo-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Book a Demo <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <p className="text-[11px] text-[#9ca3af] text-center">You'll be redirected to our calendar to pick a time.</p>
            </form>
          </div>
        </div>
      )}

      {/* NAV */}
      <nav className="bg-white border-b border-[#f0f0f0] sticky top-0 z-50" data-testid="employers-nav">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-5 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-7 group-hover:scale-105 transition-transform" />
          </button>
          <div className="flex items-center gap-2.5">
            <Button variant="outline" className="rounded-lg text-[13px] font-semibold border-[#e5e7eb] h-9 px-4" onClick={() => navigate('/login')}>Log In</Button>
            <Button className="rounded-lg text-[13px] font-semibold bg-[#D64D7A] hover:bg-[#c4406b] text-white h-9 px-4" onClick={openDemo} data-testid="nav-demo-btn">Request a Demo</Button>
          </div>
        </div>
      </nav>

      {/* SECTION 1: Hero */}
      <section className="bg-[#fafafa] border-b border-[#f0f0f0]" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <FadeIn>
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1a1d1f] tracking-tight leading-[1.1]" style={{ fontFamily: 'Manrope' }} data-testid="hero-headline">
                Give your team real benefits. Not just a bank transfer.
              </h1>
              <p className="text-base lg:text-lg text-[#6b7280] mt-6 max-w-2xl leading-relaxed">
                Most companies deposit a lump sum and call it compensation. The best ones break it down — meals, transport, health cover, pension — delivered through vendors, tracked automatically, fully compliant. Your team gets more. Your spend stays the same.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button size="lg" className="bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-13 px-7 text-base font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.25)]" onClick={openDemo} data-testid="hero-demo-btn">
                  Request a Demo <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" className="border-[#d1d5db] text-[#374151] hover:bg-[#f9fafb] rounded-xl h-13 px-7 text-base font-semibold" onClick={() => navigate('/estimator')} data-testid="hero-estimator-btn">
                  Try the Benefits Estimator
                </Button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 2: Benefits Package Story */}
      <section className="py-20 lg:py-24" data-testid="income-story-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Here's what a proper benefits package looks like.</h2>
              <p className="text-base text-[#6b7280] mt-4 max-w-2xl mx-auto">
                Most companies pay a lump sum and leave employees to figure out the rest. When you provide meals, transport, and health cover as company benefits — delivered through vendors — your team gets a complete package, not just a bank transfer.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="max-w-3xl mx-auto">
              <p className="text-center text-sm font-semibold text-[#9ca3af] uppercase tracking-wider mb-6">For a ₦500,000/month employee:</p>

              {/* Flow Breakdown */}
              <div className="bg-[#1a1d1f] rounded-xl p-6 md:p-8 mb-6" data-testid="flow-breakdown">
                <p className="text-xs font-bold text-[#00B894] uppercase tracking-wider mb-5">₦500,000 Total Package</p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Their bank account', value: '₦334,000', color: '#00B894' },
                    { label: "Pension (your company's contribution)", value: '₦35,000', color: '#6366f1' },
                    { label: 'Pension (employee contribution)', value: '₦21,280', color: '#6366f1' },
                    { label: 'Meals (delivered via company vendor)', value: '₦30,000', color: '#D64D7A' },
                    { label: 'Transport (via company arrangement)', value: '₦20,000', color: '#D64D7A' },
                    { label: 'Health cover (HMO provider)', value: '₦25,000', color: '#D64D7A' },
                    { label: 'Life insurance (insurance provider)', value: '₦10,000', color: '#D64D7A' },
                    { label: 'PAYE tax', value: '₦24,720', color: '#9ca3af' },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-[#6b7280] text-sm select-none">{i === 0 ? '├──' : i === 7 ? '└──' : '├──'}</span>
                      <span className="text-sm font-semibold" style={{ color: row.color, fontFamily: 'JetBrains Mono, monospace' }}>{row.value}</span>
                      <span className="text-sm text-[#9ca3af]">{row.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#6b7280] mt-5 pt-4 border-t border-white/10">Every naira accounted for. Your team sees exactly where their package goes.</p>
              </div>

              {/* Comparison Table — 3 rows */}
              <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden mb-6" data-testid="comparison-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#f0f0f0]">
                        <th className="text-left px-6 py-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider"></th>
                        <th className="text-right px-6 py-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Without Pebbles</th>
                        <th className="text-right px-6 py-3 text-xs font-semibold text-[#00B894] uppercase tracking-wider">With Pebbles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Your Spend Per Employee', before: '₦500,000', after: '₦500,000', bold: false },
                        { label: 'Benefits You Provide', before: '₦0', after: '₦85,000', bold: false },
                        { label: 'What They Have Left', before: '~₦306,000', after: '~₦334,000', bold: true },
                      ].map((row, i) => (
                        <tr key={i} className={`border-b border-[#f0f0f0] last:border-0 ${row.bold ? 'bg-[#fafafa]' : ''}`}>
                          <td className={`px-6 py-3 text-[#374151] ${row.bold ? 'font-bold' : ''}`}>{row.label}</td>
                          <td className={`px-6 py-3 text-right text-[#6b7280] ${row.bold ? 'font-bold text-[#374151]' : ''}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{row.before}</td>
                          <td className={`px-6 py-3 text-right ${row.bold ? 'font-bold text-[#00B894]' : 'text-[#374151]'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{row.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-center text-sm text-[#6b7280] mt-5">
                Want to see this for your actual salary bands?{' '}
                <button onClick={openDemo} className="text-[#D64D7A] font-semibold hover:underline" data-testid="income-demo-link">Request a Demo</button>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 3: What Changes */}
      <section className="bg-[#fafafa] border-y border-[#f0f0f0] py-20 lg:py-24" data-testid="what-changes-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Everything runs through one platform</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Zap, color: '#D64D7A',
                title: 'One deposit. Everything routes.',
                desc: 'Stop paying vendors separately, chasing spreadsheets, and generating payslips manually. Deposit once — Pebbles splits it across wallets, vendors, pension, and tax. Payslips generated. Remittances filed. Done.',
              },
              {
                icon: Shield, color: '#00B894',
                title: 'Your payroll stays compliant. Automatically.',
                desc: "The Nigeria Tax Act 2025 changed how payroll works — new rates, new reliefs, new penalties. Pebbles calculates, deducts, and remits under the current law. Every pay run, every employee. You don't touch it.",
              },
              {
                icon: BarChart3, color: '#6366f1',
                title: 'Your team sees what you actually provide.',
                desc: 'Employees get a dashboard showing their full package — cash, meals, transport, health cover, and the pension contribution your company makes. No more "where did my money go?" messages. Just transparency that builds trust.',
              },
            ].map((card, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="bg-white rounded-xl border border-[#e5e7eb] p-7 h-full">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${card.color}12` }}>
                    <card.icon className="w-5 h-5" style={{ color: card.color }} />
                  </div>
                  <h3 className="text-base font-bold text-[#1a1d1f] mb-2" style={{ fontFamily: 'Manrope' }}>{card.title}</h3>
                  <p className="text-sm text-[#6b7280] leading-relaxed">{card.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Benefits Delivered Properly */}
      <section className="py-20 lg:py-24" data-testid="delivery-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="max-w-2xl mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>How you deliver it determines how it's treated.</h2>
              <p className="text-base text-[#6b7280] mt-4 leading-relaxed">
                A ₦30,000 "food allowance" deposited into an employee's account is just cash with a label — and treated as such under Nigerian tax law. Meals provided through a company vendor? That's a benefit your company provides. The difference isn't the amount. It's the form.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Store, color: '#00B894', bg: 'bg-[#00B894]/8',
                title: 'Vendor payments',
                desc: 'Meals, transport, health cover routed directly to your approved providers',
                good: true,
              },
              {
                icon: Ticket, color: '#6366f1', bg: 'bg-[#6366f1]/8',
                title: 'Digital vouchers',
                desc: 'Issued to employees, tracked and reconciled through the platform',
                good: true,
              },
              {
                icon: Banknote, color: '#ef4444', bg: 'bg-red-50',
                title: 'Not this',
                desc: 'Cash "allowances" labelled as benefits but deposited into employee accounts',
                good: false,
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className={`rounded-xl border p-7 h-full ${item.good ? 'bg-white border-[#e5e7eb]' : 'bg-[#fef2f2] border-red-100'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${item.bg}`}>
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-base font-bold text-[#1a1d1f] mb-1" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                  <p className={`text-sm leading-relaxed ${item.good ? 'text-[#6b7280]' : 'text-red-400'}`}>{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <p className="text-sm text-[#9ca3af] mt-6">Pebbles ensures every benefit reaches your team in the form the law recognises.</p>
        </div>
      </section>

      {/* SECTION 5: New Tax Law */}
      <section className="bg-[#1a1d1f] text-white py-20 lg:py-24" data-testid="tax-law-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope' }}>The rules changed in January. Most companies haven't caught up.</h2>
              <p className="text-base text-[#9ca3af] mt-5 leading-relaxed">
                The Nigeria Tax Act 2025 took effect January 2026. New PAYE rates. Old reliefs abolished. Steeper penalties for getting payroll wrong. New incentives for companies that structure benefits and create jobs.
              </p>
              <p className="text-base text-white font-medium mt-4 leading-relaxed">
                If your payroll is still running on the old rules, every payslip you've issued since January may be wrong.
              </p>
              <div className="mt-8">
                <button onClick={openDemo} className="inline-flex items-center gap-2 text-[#D64D7A] font-semibold text-base hover:underline" data-testid="tax-demo-btn">
                  Talk to us about what changed and how it affects your company <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 6: How It Works */}
      <section className="py-20 lg:py-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Live in days. Not weeks.</h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Upload, num: '1', title: 'Send us your team list', desc: 'CSV upload or add employees individually. Wallets are created instantly.' },
              { icon: Settings2, num: '2', title: 'Set your benefits menu', desc: 'Choose which benefits to offer: meals, transport, health cover, devices, and more. Set allocations and approved vendors.' },
              { icon: Users, num: '3', title: 'Employees choose', desc: 'Your team logs in and picks the benefits that work for them, within your framework.' },
              { icon: Play, num: '4', title: 'Run payroll once', desc: 'Deposit your total amount. Pebbles routes cash to wallets, benefits to vendors, deductions to the right places. Payslips generated. Done.' },
            ].map((step, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-[#D64D7A]/10 flex items-center justify-center text-sm font-extrabold text-[#D64D7A]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{step.num}</div>
                    <step.icon className="w-5 h-5 text-[#9ca3af]" />
                  </div>
                  <h3 className="text-base font-bold text-[#1a1d1f] mb-2" style={{ fontFamily: 'Manrope' }}>{step.title}</h3>
                  <p className="text-sm text-[#6b7280] leading-relaxed">{step.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: Built for Nigerian Companies */}
      <section className="bg-[#fafafa] border-y border-[#f0f0f0] py-20 lg:py-24" data-testid="built-for-ng-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Not another foreign HR tool.</h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: Globe, text: 'Naira-native — no currency conversion, no foreign platform fees' },
              { icon: Store, text: 'Your vendors — works with Shoprite, your HMO, transport operators, any approved vendor' },
              { icon: Building2, text: 'Multi-company — manage subsidiaries and entities from one dashboard' },
              { icon: Shield, text: 'NTA 2025 compliant — built for the current law, not patched onto the old one' },
              { icon: Landmark, text: 'Assets MFB integration — employee wallets, instant settlement, real-time reconciliation' },
              { icon: Heart, text: 'Pension visibility — your team sees the pension contribution you make, alongside their own' },
              { icon: Clock, text: '70% enrollment in 30 days — average across companies we\u2019ve launched' },
            ].map((item, i) => (
              <FadeIn key={i} delay={0.03 * (i + 1)}>
                <div className="flex items-start gap-3 bg-white rounded-lg border border-[#e5e7eb] p-5">
                  <item.icon className="w-5 h-5 text-[#D64D7A] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#374151] leading-relaxed">{item.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: Social Proof */}
      <section className="py-16 lg:py-20" data-testid="social-proof-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a1d1f] text-center mb-10" style={{ fontFamily: 'Manrope' }}>Trusted by Nigerian companies</h2>
          </FadeIn>
          <div className="flex flex-wrap justify-center gap-8 lg:gap-16">
            {[
              { val: '500+', label: 'Companies Launched' },
              { val: '45,000+', label: 'Employees Onboarded' },
              { val: '₦85K', label: 'Avg. Monthly Benefits Per Employee' },
              { val: '70%', label: 'Avg. Enrollment in 30 Days' },
            ].map((stat, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-extrabold text-[#D64D7A]" style={{ fontFamily: 'Manrope' }}>{stat.val}</p>
                  <p className="text-sm text-[#6b7280] mt-1">{stat.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: Final CTA */}
      <section className="bg-[#1a1d1f] py-20 lg:py-28" data-testid="final-cta-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>See what a proper benefits package looks like for your team</h2>
            <p className="text-base text-[#9ca3af] mt-4 max-w-2xl mx-auto leading-relaxed">
              We'll model your actual salary bands, show you the full breakdown — what your team takes home, what you provide, what it costs you — and walk you through what changed under NTA 2025. 30 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button size="lg" className="bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-13 px-8 text-base font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.25)]" onClick={openDemo} data-testid="final-demo-btn">
                Request a Demo <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-xl h-13 px-8 text-base font-semibold" onClick={() => navigate('/estimator')} data-testid="final-estimator-btn">
                Try the Benefits Estimator
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#f0f0f0] py-8" data-testid="employers-footer">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-6 opacity-60" />
            <div className="flex items-center gap-4 text-sm text-[#9ca3af]">
              <button onClick={() => navigate('/')} className="hover:text-[#1a1d1f] transition-colors">Home</button>
              <button onClick={() => navigate('/estimator')} className="hover:text-[#1a1d1f] transition-colors">Estimator</button>
              <button onClick={() => navigate('/login')} className="hover:text-[#1a1d1f] transition-colors">Log In</button>
            </div>
          </div>
          <p className="text-[11px] text-[#9ca3af] leading-relaxed max-w-4xl">
            The information on this page is for general guidance only and does not constitute tax, legal, or financial advice. Benefits described refer to goods and services provided directly by the employer through approved vendors and providers, not cash allowances. Tax treatment of benefits depends on form of delivery, documentation, employer configuration, and compliance with the Nigeria Tax Act 2025 and Nigeria Tax Administration Act 2025. Pebbles Technologies Ltd does not provide tax advice. Consult a qualified tax professional for guidance specific to your company.
          </p>
          <p className="text-[11px] text-[#c4c8cc] mt-3">Pebbles Technologies Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
